/**
 * Dialog „Absender, Empfänger & Richtung" (Kundenfeedback 02.10.2026, erster
 * Teil, III; Fehlermeldung Kanzlei Polz 16.10.2026): Wer sich eine E-Mail
 * selbst an die zentrale Unternehmens-E-Mail-Adresse schickt oder sie aus
 * Outlook mit KARS im Bcc versendet, legt hier fest, ob sie eingegangen oder
 * gesendet ist, von wem sie stammt bzw. an wen sie ging – bei gesendeten
 * Mails als Liste aller Empfänger (An/Cc), nachträglich änderbar. Reine
 * Logik ohne Vue – siehe DirectionDialog.vue für die Oberfläche.
 */
import type { MessageDirection } from './chatApi'
import { normalizeEmail } from './chatModel'

export type ClassifyRecipientRole = 'to' | 'cc'

export interface ClassifyRecipient {
  email: string
  name: string
  role: ClassifyRecipientRole
}

/** Antwort von GET /protected/messages/{id}/direction. */
export interface DirectionInfo {
  direction: MessageDirection
  senderName: string
  senderEmail: string
  /** Im Text steht ein weitergeleiteter Kopfblock („Von: … Gesendet: …"). */
  forwardedDetected: boolean
  suggestedName: string
  suggestedEmail: string
  /** Externe An/Cc-Teilnehmer, unter denen der Verlauf bisher steht. */
  counterparts: Array<{ email: string, name: string, role?: string | null }>
  /** Unterhaltung besteht nur aus dieser E-Mail: Die Angabe ersetzt die Gegenseite. */
  singleMessage: boolean
  /** Unterhaltung gehört zu einer Akte: Beteiligte bleiben erhalten, nur die eigene Absender-Adresse wird ersetzt. */
  inCase: boolean
  /** Unterhaltung besteht nur aus dieser E-Mail (mit oder ohne Akte): Eine Empfängerliste ersetzt die bisherigen Empfänger. */
  onlyMessage?: boolean
}

export interface ClassifyForm {
  direction: MessageDirection
  /** Absender bei „eingegangen". */
  email: string
  name: string
  /** Empfänger (An/Cc) bei „gesendet". */
  recipients: ClassifyRecipient[]
}

export interface ClassifySuggestion {
  email: string
  name: string
  source: 'forwarded' | 'counterpart'
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export function isValidEmail (value: string): boolean {
  return EMAIL_PATTERN.test(value.trim())
}

function normalizeRole (role?: string | null): ClassifyRecipientRole {
  return String(role || '').trim().toLowerCase() === 'cc' ? 'cc' : 'to'
}

function normalizeRecipient (r: { email: string, name?: string | null, role?: string | null }): ClassifyRecipient {
  return { email: normalizeEmail(r.email), name: (r.name || '').trim(), role: normalizeRole(r.role) }
}

/** Bisherige Gegenseite als Empfängerliste (Rollen An/Cc bleiben erhalten). */
function recipientsFromInfo (info: DirectionInfo): ClassifyRecipient[] {
  const out: ClassifyRecipient[] = []
  for (const c of info.counterparts) {
    const r = normalizeRecipient(c)
    if (r.email && !out.some(x => x.email === r.email)) out.push(r)
  }
  return out
}

/**
 * Vorbelegung: Eine erkannte Weiterleitung ist in aller Regel eine
 * empfangene E-Mail des ursprünglichen Absenders. Sonst bleibt die aktuelle
 * Einordnung mit dem geführten Absender bzw. der bisherigen Gegenseite als
 * Empfängerliste.
 */
export function initialClassifyForm (info: DirectionInfo): ClassifyForm {
  const recipients = recipientsFromInfo(info)
  if (info.forwardedDetected && info.suggestedEmail) {
    return { direction: 'in', email: normalizeEmail(info.suggestedEmail), name: info.suggestedName || '', recipients }
  }
  if (info.direction === 'in') {
    return { direction: 'in', email: normalizeEmail(info.senderEmail), name: info.senderName || '', recipients }
  }
  return { direction: 'out', email: '', name: '', recipients }
}

/** Empfänger ergänzen (normalisiert); eine bereits eingetragene Adresse ändert nichts. */
export function addRecipient (form: ClassifyForm, recipient: { email: string, name?: string | null, role?: string | null }): ClassifyForm {
  const next = normalizeRecipient(recipient)
  if (!next.email || form.recipients.some(r => r.email === next.email)) {
    return form
  }
  return { ...form, recipients: [...form.recipients, next] }
}

export function removeRecipient (form: ClassifyForm, index: number): ClassifyForm {
  return { ...form, recipients: form.recipients.filter((_, i) => i !== index) }
}

export function updateRecipient (form: ClassifyForm, index: number, patch: Partial<ClassifyRecipient>): ClassifyForm {
  return {
    ...form,
    recipients: form.recipients.map((r, i) => (i === index ? { ...r, ...patch } : r)),
  }
}

/** Fehlermeldung (deutsche Quelle für $gt) oder '' wenn die Angaben passen. */
export function validateClassify (form: ClassifyForm): string {
  if (form.direction === 'in') {
    const email = form.email.trim()
    if (!email) {
      return 'Bitte die E-Mail-Adresse des Absenders angeben'
    }
    return isValidEmail(email) ? '' : 'Bitte eine gültige E-Mail-Adresse angeben'
  }
  const seen = new Set<string>()
  for (const r of form.recipients) {
    const email = normalizeEmail(r.email)
    if (!email) continue
    if (!isValidEmail(email)) {
      return 'Bitte eine gültige E-Mail-Adresse angeben'
    }
    if (seen.has(email)) {
      return 'Eine E-Mail-Adresse ist doppelt eingetragen'
    }
    seen.add(email)
  }
  return ''
}

export interface ClassifyPayload {
  direction: MessageDirection
  counterpartEmail: string
  counterpartName: string
  /** Empfänger (An/Cc) bei „gesendet"; leer bei „eingegangen". */
  recipients: ClassifyRecipient[]
}

export function classifyPayload (form: ClassifyForm): ClassifyPayload {
  if (form.direction === 'in') {
    const counterpartEmail = normalizeEmail(form.email)
    return {
      direction: 'in',
      counterpartEmail,
      counterpartName: counterpartEmail ? form.name.trim() : '',
      recipients: [],
    }
  }
  const recipients: ClassifyRecipient[] = []
  for (const r of form.recipients) {
    const next = normalizeRecipient(r)
    if (next.email && !recipients.some(x => x.email === next.email)) recipients.push(next)
  }
  const first = recipients[0]
  return {
    direction: 'out',
    counterpartEmail: first?.email || '',
    counterpartName: first?.name || '',
    recipients,
  }
}

/** Auswahlvorschläge: erkannter ursprünglicher Absender, dann die bisherige Gegenseite. */
export function suggestionChips (info: DirectionInfo): ClassifySuggestion[] {
  const all: ClassifySuggestion[] = [
    ...(info.forwardedDetected && info.suggestedEmail
      ? [{ email: normalizeEmail(info.suggestedEmail), name: info.suggestedName || '', source: 'forwarded' as const }]
      : []),
    ...info.counterparts.map(c => ({ email: normalizeEmail(c.email), name: c.name || '', source: 'counterpart' as const })),
  ]
  return all.filter((chip, i) => chip.email && all.findIndex(other => other.email === chip.email) === i)
}
